import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Users, Lightbulb } from 'lucide-react'; // Added Lucide icons

const AboutUs = () => {
    // Intersection Observer logic for fade-in animations
    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                // Remove initial opacity and transform classes if they were set for the animation
                entry.target.classList.remove('opacity-0', 'translate-y-10');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1 // Trigger when 10% of the element is visible
        });

        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer; // Store observer reference on the element
            }
        });

        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleSectionIntersect]);

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Global Styles for animations (same as App.js for consistency) */}
            <style>
                {`
                .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                .fade-in-up-active { opacity: 1; transform: translateY(0); }
                `}
            </style>

            {/* Section 1: Hero/Introduction - Background with image */}
            <section
                className="py-20 text-center px-6 relative overflow-hidden bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url('https://i.imgur.com/9sS8ej3.jpeg')`,
                }}
            >
                {/* Overlay for dark background and improved text readability */}
                <div className="absolute inset-0 bg-black bg-opacity-20 z-0"></div> {/* Adjusted opacity to 20% */}
                <div className="container mx-auto relative z-10 flex flex-col items-center justify-center">
                    {/* Added a div for the rounded black overlay */}
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        <h1 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-6">
                            About JAKOM
                        </h1>
                        <p className="text-xl md:text-2xl text-[#F8F8F8] max-w-3xl mx-auto mb-10">
                            JAKOM is more than just a service provider; we are your dedicated partner in navigating the complexities of modern business. Our mission is to simplify operations, enhance efficiency, and foster growth for enterprises of all sizes through innovative tech solutions and unparalleled expertise.
                        </p>
                        <Link to="/contact-us" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 2: Mission, Vision, Values - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12 fade-in-up" ref={setSectionRef}>
                        Our Core Principles
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {/* Mission Card */}
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.1s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Target size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Our Mission</h3>
                            <p className="text-gray-700">To empower businesses with seamless, integrated tech solutions that drive efficiency, foster growth, and simplify operations.</p>
                        </div>
                        {/* Vision Card */}
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.2s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Eye size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Our Vision</h3>
                            <p className="text-gray-700">To be the leading one-stop tech solutions provider globally, recognized for innovation, reliability, and unparalleled client success.</p>
                        </div>
                        {/* Values Card */}
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.3s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Lightbulb size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Our Values</h3>
                            <ul className="list-disc list-inside text-gray-700 text-left pl-4">
                                <li>**Innovation:** Constantly seeking new and better solutions.</li>
                                <li>**Integrity:** Operating with honesty and transparency.</li>
                                <li>**Client-Centricity:** Prioritizing client needs and success.</li>
                                <li>**Excellence:** Delivering high-quality results in every service.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 3: Meet Our Team (Placeholder) - Blue Background */}
            <section className="py-20 bg-[#0A1128] border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-8 fade-in-up" ref={setSectionRef}>
                        Meet Our Dedicated Team
                    </h2>
                    <p className="text-xl text-[#CCD2E3] mb-10 fade-in-up" ref={setSectionRef}>
                        Behind every successful solution is a team of passionate experts. Learn more about the individuals driving JAKOM's innovation and commitment to excellence.
                    </p>
                    <Link to="/contact-us" className="px-12 py-4 bg-[#C9B072] text-[#0A1128] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}>
                        Discover Our Experts
                    </Link>
                </div>
            </section>

            {/* Section 4: Call to Action - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-8 fade-in-up" ref={setSectionRef}>
                        Ready to Partner with JAKOM?
                    </h2>
                    <p className="text-xl text-[#333333] mb-10 fade-in-up" ref={setSectionRef}>
                        We're eager to help you achieve your business goals. Reach out today!
                    </p>
                    <Link to="/get-a-quote" className="px-12 py-4 bg-[#4CAF50] text-[#F8F8F8] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}>
                        Get a Free Consultation
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default AboutUs;
